import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

type User = any;

interface AuthContextType {
  user: User | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ success: boolean; error?: any }>;
  signOut: () => Promise<void>;
  resetIdleTimer: () => void;
  getActiveSession: () => Promise<any>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const idleTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const userRef = useRef<User | null>(null);
  const navigate = useNavigate();

  // Keep userRef in sync
  useEffect(() => {
    userRef.current = user;
  }, [user]);

  // Sign out function - stable reference using ref for timeout
  const signOut = useCallback(async () => {
    try {
      await supabase.auth.signOut();
      setUser(null);
      if (idleTimeoutRef.current) clearTimeout(idleTimeoutRef.current);
      navigate('/login');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  }, [navigate]);

  // Stable resetIdleTimer - no state dependencies
  const resetIdleTimer = useCallback(() => {
    if (idleTimeoutRef.current) clearTimeout(idleTimeoutRef.current);
    
    idleTimeoutRef.current = setTimeout(() => {
      signOut();
      toast("Session expired", {
        description: "You have been logged out due to inactivity",
      });
    }, 600000); // 10 minutes
  }, [signOut]);

  // Function to get the active session
  const getActiveSession = useCallback(async () => {
    try {
      console.log("Getting active session");
      const { data, error } = await supabase.auth.getSession();
      
      if (error) {
        console.error('Error getting session:', error);
        return null;
      }
      
      if (data.session && data.session.user) {
        console.log("Session found, user:", data.session.user.id);
        // Only update user state if it's different to prevent re-renders
        if (!userRef.current || userRef.current.id !== data.session.user.id) {
          console.log("Updating user state with new user data");
          setUser(data.session.user);
        }
        return data.session;
      }
      
      return data.session;
    } catch (error) {
      console.error('Error getting active session:', error);
      return null;
    }
  }, []); // Stable - uses ref instead of user state

  // Check for existing session on mount ONLY
  useEffect(() => {
    const checkUser = async () => {
      try {
        console.log("Checking for existing session");
        const { data, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error('Error checking session:', error);
          setLoading(false);
          return;
        }
        
        if (data.session) {
          console.log("Found existing session, user:", data.session.user.id);
          setUser(data.session.user);
          resetIdleTimer();
        }
      } catch (error) {
        console.error('Error checking session:', error);
      } finally {
        setLoading(false);
      }
    };
    
    checkUser();
    
    // Listen for auth state changes
    const { data: authListener } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log('Auth state changed:', event, session ? session.user?.id : 'no session');
        
        if (event === 'SIGNED_IN' && session) {
          console.log("User signed in:", session.user.id);
          setUser(session.user);
          resetIdleTimer();
        } else if (event === 'SIGNED_OUT') {
          console.log("User signed out");
          setUser(null);
          if (idleTimeoutRef.current) clearTimeout(idleTimeoutRef.current);
        } else if (event === 'TOKEN_REFRESHED' && session) {
          console.log("Token refreshed for user:", session.user.id);
          // Guard: only update if user ID changed
          if (!userRef.current || userRef.current.id !== session.user.id) {
            setUser(session.user);
          }
          resetIdleTimer();
        } else if (event === 'USER_UPDATED' && session) {
          console.log("User updated:", session.user.id);
          setUser(session.user);
          resetIdleTimer();
        }
      }
    );
    
    // Set up activity listeners to reset the idle timer
    const handleActivity = () => {
      if (userRef.current) resetIdleTimer();
    };
    
    window.addEventListener('mousedown', handleActivity);
    window.addEventListener('keydown', handleActivity);
    window.addEventListener('touchstart', handleActivity);
    
    return () => {
      if (idleTimeoutRef.current) clearTimeout(idleTimeoutRef.current);
      authListener.subscription.unsubscribe();
      window.removeEventListener('mousedown', handleActivity);
      window.removeEventListener('keydown', handleActivity);
      window.removeEventListener('touchstart', handleActivity);
    };
  }, []); // Run once on mount - no circular dependencies

  // Check session status periodically
  useEffect(() => {
    const checkSessionStatus = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        
        if (session) {
          const expiryTime = new Date(session.expires_at! * 1000);
          const fifteenMinutes = 15 * 60 * 1000;
          
          if (expiryTime.getTime() - Date.now() < fifteenMinutes) {
            const { error } = await supabase.auth.refreshSession();
            if (error) {
              await supabase.auth.signOut();
              setUser(null);
              navigate('/login');
            }
          }
        }
      } catch (error) {
        console.error('Session check failed:', error);
        await supabase.auth.signOut();
        setUser(null);
        navigate('/login');
      }
    };
    
    const interval = setInterval(checkSessionStatus, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [navigate]);

  const signIn = async (email: string, password: string) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      
      if (error) {
        return { success: false, error };
      }
      
      setUser(data.user);
      resetIdleTimer();
      return { success: true };
    } catch (error) {
      return { success: false, error };
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, signIn, signOut, resetIdleTimer, getActiveSession }}>
      {children}
    </AuthContext.Provider>
  );
};
